/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { ILogger } from './logger/interfaces';
import { LogCategory } from './logger/LogCategories';

// This service worker lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
        // [::1] is the IPv6 localhost address.
        window.location.hostname === '[::1]' ||
        // 127.0.0.0/8 are considered localhost for IPv4.
        window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

const serviceWorkerUrl = '/serviceworker.js';

/**
 * Utility function to register a service worker.
 */
export function register(logger: ILogger): void {
    logger.logTraceInfo(LogCategory.ServiceWorkerRegistration, '[SW:register] register called');
    if ('serviceWorker' in navigator && !isLocalhost) {
        registerValidSW(serviceWorkerUrl, logger);
    } else {
        logger.logTraceInfo(
            LogCategory.ServiceWorkerRegistration,
            `[SW:register] SW not present in navigator or isLocalhost:${isLocalhost}`
        );
    }
}

/**
 * Registers a valid service worker.
 *
 * @param swUrl The URL of the service worker.
 */
function registerValidSW(swUrl: string, logger: ILogger): void {
    logger.logTraceInfo(LogCategory.ServiceWorkerRegistration, '[SW:registerValidSW] registerValidSW called');
    navigator.serviceWorker
        .register(swUrl)
        .then((registration: ServiceWorkerRegistration): void => {
            registration.onupdatefound = (): void => {
                const installingWorker = registration.installing;

                if (installingWorker == null) {
                    return;
                }

                installingWorker.onstatechange = (): void => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            // At this point, the updated precached content has been fetched,
                            // but the previous service worker will still serve the older
                            // content until all client tabs are closed.
                            logger.logTraceInfo(
                                LogCategory.ServiceWorkerRegistration,
                                '[SW:registerValidSW] New Content is available'
                            );
                        } else {
                            // At this point, everything has been precached.
                            logger.logTraceInfo(
                                LogCategory.ServiceWorkerRegistration,
                                '[SW:registerValidSW] Static assets are cached for offline use'
                            );
                        }
                    }
                };
            };
        })
        .catch((error: Error): void => {
            logger.logTraceError(LogCategory.ServiceWorkerRegistration, `[SW:registerValidSW] Error ${error.message}`);
        });
}

export function unregister(logger: ILogger): void {
    logger.logTraceInfo(LogCategory.ServiceWorkerRegistration, '[SW:unregister] unregister called');
    if ('serviceWorker' in navigator && navigator.serviceWorker !== null) {
        navigator.serviceWorker.ready
            .then((registration: ServiceWorkerRegistration): void => {
                logger.logTraceInfo(
                    LogCategory.ServiceWorkerRegistration,
                    '[SW:unregister] unregistering the service worker'
                );
                registration.unregister().then((result: boolean): void => {
                    logger.logTraceInfo(LogCategory.ServiceWorkerRegistration, `[SW:unregister] result:${result}`);
                });
            })
            .catch((error: Error): void => {
                logger.logTraceError(LogCategory.ServiceWorkerRegistration, `[SW:unregister] error ${error.message}`);
            });
    }
}
